import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { firstValueFrom } from 'rxjs';
import { I18nService } from '../../../../core';
import { AuthService } from '../../../../core/services/auth.service';
import { AppModalComponent, HexColorPickerPopoverComponent, ToastService } from '../../../../shared/ui';
import { PRIMARY_HEX_PALETTE, isLightHexColor } from '../../../../shared/utils/hex-color.util';
import {
  WORKFLOW_ACCENT_COLOR_PALETTE,
  WORKFLOW_FORM_ICON_KEYS,
} from '../../../../shared/utils/workflow-form-appearance.util';
import { workflowIconKeyToApi } from '../../../../shared/utils/workflow-icon-key.util';
import { projectIconKeyToApi } from '../../../../shared/utils/project-icon-key.util';

type CreateProjectResponse = { id?: string };
type CreateWorkflowResponse = { id?: string };
type WizardStep = 1 | 2 | 3;

interface WorkflowTemplateOption {
  id: string;
  title: string;
  description: string;
}

interface StageDraft {
  id: string;
  name: string;
  colorHex: string;
  stageType: 1 | 2 | 3;
  order: number;
}

type ProjectMemberRole = 'Owner' | 'Editor' | 'Viewer';

interface MemberDraft {
  email: string;
  role: ProjectMemberRole;
}

interface CompanyUser {
  id: string;
  email: string;
  name: string;
}

interface WizardPhase {
  id: WizardStep;
}

const WORKFLOW_TEMPLATES: WorkflowTemplateOption[] = [
  { id: 'starter', title: 'Inicial', description: 'Para tarefas operacionais do dia a dia' },
  { id: 'marketing', title: 'Marketing', description: 'Campanhas e calendário de conteúdo' },
  { id: 'project-management', title: 'Gestão de Projetos', description: 'Planejamento e execução de projetos' },
  { id: 'product-engineering', title: 'Produto + Engenharia', description: 'Fluxo de produto e engenharia' },
];

@Component({
  selector: 'app-create-project-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, AppModalComponent, HexColorPickerPopoverComponent],
  template: `
    <app-modal
      [visible]="visible()"
      size="md"
      [showCloseButton]="false"
      [title]="modalTitle()"
      [subtitle]="modalSubtitle()"
      (closed)="onClose()"
    >
      <div modalHeaderActions class="phase-inline">
        <span class="phase-inline-label">Fase {{ step() }} de 3</span>
        <div class="phase-inline-track">
          @for (phase of phases; track phase.id; let index = $index) {
            <span class="phase-inline-dot" [class.is-active]="step() === phase.id">{{ phase.id }}</span>
            @if (index < phases.length - 1) {
              <span class="phase-inline-line"></span>
            }
          }
        </div>
      </div>

      <div modalBody class="wizard-body" (click)="closeStageColorPicker()">
        @if (step() === 1) {
          <section class="wizard-section">
            <h3 class="section-title">Ícone e nome</h3>

            <div class="icon-name-row">
              <button
                type="button"
                class="icon-preview-btn"
                [class.has-color]="!!selectedColor()"
                [style.background]="selectedColor() || 'var(--bg-surface)'"
                [style.borderColor]="selectedColor() || 'var(--border)'"
                (click)="toggleIconPicker()"
              >
                @if (uploadedImageDataUrl(); as imageUrl) {
                  <img class="icon-preview-image" [src]="imageUrl" alt="Imagem do projeto" />
                } @else if (selectedIcon(); as iconName) {
                  <lucide-icon [name]="iconName" class="h-4 w-4 icon-preview-glyph"></lucide-icon>
                } @else {
                  <lucide-icon name="mouse-pointer" class="h-4 w-4 icon-preview-empty-glyph"></lucide-icon>
                }
              </button>
              <input
                type="text"
                class="field-input"
                [ngModel]="name()"
                (ngModelChange)="name.set($event)"
                placeholder="Ex.: Marketing, Operações, Engenharia"
              />

              @if (isIconPickerOpen()) {
                <div class="icon-picker" (click)="$event.stopPropagation()">
                  <div class="icon-picker-head">
                    <strong>Ícone do projeto</strong>
                    <div class="picker-head-actions">
                      <button type="button" class="upload-btn head-upload-btn" (click)="imageInput.click()">Upload</button>
                      <button type="button" (click)="isIconPickerOpen.set(false)">Fechar</button>
                    </div>
                  </div>
                  <div class="picker-actions">
                    <div class="picker-color-inline">
                      <button
                        type="button"
                        class="color-choice compact color-choice-none"
                        [class.is-selected]="!selectedColor()"
                        (click)="selectedColor.set(null)"
                        title="Sem cor"
                      >
                        <span class="none-mark" aria-hidden="true"></span>
                        <span class="sr-only">Sem cor</span>
                      </button>
                      @for (color of colorOptions; track color) {
                        <button
                          type="button"
                          class="color-choice compact"
                          [style.background]="color"
                          [class.is-selected]="color === selectedColor()"
                          (click)="selectedColor.set(color)"
                        >
                          <span class="sr-only">Selecionar cor {{ color }}</span>
                        </button>
                      }
                    </div>
                  </div>

                  @if (uploadedImageName()) {
                    <div class="upload-file-name inline">{{ uploadedImageName() }}</div>
                  }
                  <input
                    #imageInput
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    class="upload-input"
                    (change)="onProjectImageSelected($event)"
                  />

                  <div class="icon-grid">
                    @for (icon of filteredIcons(); track icon) {
                      <button
                        type="button"
                        class="icon-choice"
                        [class.is-selected]="icon === selectedIcon()"
                        (click)="selectIcon(icon)"
                      >
                        <lucide-icon [name]="icon" class="h-4 w-4"></lucide-icon>
                      </button>
                    }
                  </div>

                  @if (uploadedImageName()) {
                    <div class="picker-actions">
                      <button type="button" class="clear-upload-btn" (click)="removeUploadedImage()">Remover imagem</button>
                    </div>
                  }
                </div>
              }
            </div>

            <label class="field-label">
              Descrição <span class="optional">(opcional)</span>
              <textarea
                class="field-textarea"
                rows="3"
                [ngModel]="description()"
                (ngModelChange)="description.set($event)"
              ></textarea>
            </label>

            <div class="inline-row">
              <div>
                <p class="inline-title">Projeto privado</p>
                <p class="inline-subtitle">Somente você e membros convidados terão acesso</p>
              </div>
              <label class="switch">
                <input
                  type="checkbox"
                  [ngModel]="isPrivate()"
                  (ngModelChange)="isPrivate.set($event)"
                />
                <span class="switch-track"><span class="switch-thumb"></span></span>
              </label>
            </div>
          </section>
        } @else if (step() === 2) {
          <section class="wizard-section">
            <h3 class="section-title">Template do workflow</h3>
            <div class="template-grid">
              @for (template of templates(); track template.id) {
                <button
                  type="button"
                  class="template-card"
                  [class.is-active]="template.id === selectedTemplate()"
                  (click)="selectedTemplate.set(template.id)"
                >
                  <strong>{{ template.title }}</strong>
                  <span>{{ template.description }}</span>
                </button>
              }
            </div>

            <h3 class="section-title">Configuração inicial</h3>
            <div class="workflow-form-grid">
              <label class="field-label">
                Nome do workflow
                <div class="icon-name-row">
                  <button
                    type="button"
                    class="icon-preview-btn"
                    [class.has-color]="!!workflowColorHex()"
                    [style.background]="workflowColorHex() || 'var(--bg-surface)'"
                    [style.borderColor]="workflowColorHex() || 'var(--border)'"
                    (click)="toggleIconPicker()"
                  >
                    @if (selectedIcon(); as iconName) {
                      <lucide-icon [name]="iconName" class="h-4 w-4 icon-preview-glyph"></lucide-icon>
                    } @else {
                      <lucide-icon name="columns-3" class="h-4 w-4 icon-preview-empty-glyph"></lucide-icon>
                    }
                  </button>

                  <input
                    type="text"
                    class="field-input"
                    [ngModel]="workflowName()"
                    (ngModelChange)="workflowName.set($event)"
                    placeholder="Ex.: Backlog"
                  />

                  @if (isIconPickerOpen()) {
                    <div class="icon-picker" (click)="$event.stopPropagation()">
                      <div class="icon-picker-head">
                        <strong>Ícone do workflow</strong>
                        <div class="picker-head-actions">
                          <button type="button" (click)="isIconPickerOpen.set(false)">Fechar</button>
                        </div>
                      </div>

                      <div class="picker-actions">
                        <div class="picker-color-inline">
                          @for (color of colorOptions; track color) {
                            <button
                              type="button"
                              class="color-choice compact"
                              [style.background]="color"
                              [class.is-selected]="color === workflowColorHex()"
                              (click)="workflowColorHex.set(color)"
                            >
                              <span class="sr-only">Selecionar cor {{ color }}</span>
                            </button>
                          }
                        </div>
                      </div>

                      <div class="icon-grid">
                        @for (icon of filteredIcons(); track icon) {
                          <button
                            type="button"
                            class="icon-choice"
                            [class.is-selected]="icon === selectedIcon()"
                            (click)="selectIcon(icon)"
                          >
                            <lucide-icon [name]="icon" class="h-4 w-4"></lucide-icon>
                          </button>
                        }
                      </div>
                    </div>
                  }
                </div>
              </label>
            </div>

            <label class="field-label">
              Descrição <span class="optional">(opcional)</span>
              <textarea
                class="field-textarea"
                rows="2"
                [ngModel]="workflowDescription()"
                (ngModelChange)="workflowDescription.set($event)"
              ></textarea>
            </label>

            <label class="field-label">
              Prazo padrão (dias) <span class="optional">(opcional)</span>
              <input
                type="number"
                class="field-input"
                min="0"
                step="1"
                [ngModel]="defaultDueInDays()"
                (ngModelChange)="onDefaultDueInDaysChange($event)"
                placeholder="Ex.: 3"
              />
              <span class="field-help">Null = sem prazo automático. 0 = hoje. > 0 = hoje + N dias.</span>
            </label>

            <h3 class="section-title">Status (stages)</h3>
            <div class="stages-list">
              @for (stage of stages(); track stage.id; let index = $index) {
                <article class="stage-row">
                  <div class="stage-order-cell">
                    <button
                      type="button"
                      class="stage-order stage-order-button"
                      [class.light-order]="isLightHexColor(stage.colorHex || stageColorFallback)"
                      [style.background]="stage.colorHex || stageColorFallback"
                      [title]="'Trocar cor da etapa ' + (index + 1)"
                      (click)="toggleStageColorPicker(stage.id, $event)"
                    >
                      {{ index + 1 }}
                    </button>
                    @if (openedStageColorPickerId() === stage.id) {
                      <app-hex-color-picker-popover
                        class="stage-color-picker-host"
                        [value]="stage.colorHex"
                        [fallbackHex]="stageColorFallback"
                        placement="after-host"
                        (valueCommit)="updateStageColor(stage.id, $event)"
                        (requestClose)="closeStageColorPicker()"
                      />
                    }
                  </div>
                  <input
                    type="text"
                    class="field-input stage-input"
                    [ngModel]="stage.name"
                    (ngModelChange)="updateStageName(stage.id, $event)"
                  />
                  <select
                    class="permission-select stage-type"
                    [ngModel]="stage.stageType"
                    (ngModelChange)="updateStageType(stage.id, $event)"
                  >
                    <option [ngValue]="1">Inicial</option>
                    <option [ngValue]="2">Processo</option>
                    <option [ngValue]="3">Final</option>
                  </select>
                  <button type="button" class="stage-icon-btn" [disabled]="index === 0" (click)="moveStage(index, -1)">
                    ↑
                  </button>
                  <button
                    type="button"
                    class="stage-icon-btn"
                    [disabled]="index === stages().length - 1"
                    (click)="moveStage(index, 1)"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    class="stage-icon-btn danger"
                    [disabled]="stages().length <= 1"
                    (click)="removeStage(stage.id)"
                  >
                    ×
                  </button>
                </article>
              }
            </div>

            <div class="stage-add-row">
              <input
                type="text"
                class="field-input"
                [ngModel]="newStageName()"
                (ngModelChange)="newStageName.set($event)"
                placeholder="Novo status"
              />
              <button type="button" class="btn-secondary" (click)="addStage()">Adicionar</button>
            </div>
          </section>
        } @else {
          <section class="wizard-section">
            <h3 class="section-title">Membros do projeto</h3>
            <p class="inline-subtitle">Adicione os e-mails de quem vai participar deste projeto.</p>

            <div class="member-input-row">
              <input
                type="email"
                class="field-input"
                [ngModel]="memberEmailInput()"
                (ngModelChange)="memberEmailInput.set($event)"
                placeholder="email@empresa.com"
                (keyup.enter)="addMember()"
              />
              <select
                class="permission-select member-role-select"
                [ngModel]="memberRoleInput()"
                (ngModelChange)="memberRoleInput.set($event)"
              >
                <option value="Owner">{{ t('projects.members.roles.owner') }}</option>
                <option value="Editor">{{ t('projects.members.roles.editor') }}</option>
                <option value="Viewer">{{ t('projects.members.roles.viewer') }}</option>
              </select>
              <button type="button" class="btn-secondary" (click)="addMember()">Adicionar</button>
            </div>

            <div class="member-chip-list">
              @for (member of members(); track member) {
                <span class="member-chip">
                  {{ member.email }}
                  <span class="member-chip-role">{{ roleLabel(member.role) }}</span>
                  <button type="button" class="member-chip-remove" (click)="removeMember(member.email)">×</button>
                </span>
              }
              @if (!members().length) {
                <p class="member-empty">Nenhum membro adicionado.</p>
              }
            </div>

            <p class="phase-note">
              O template selecionado será aplicado e o workflow inicial será criado ao finalizar.
            </p>
          </section>
        }
      </div>

      <div modalFooter class="wizard-footer">
        @if (step() === 1) {
          <div class="wizard-footer-actions">
            <button type="button" class="btn-secondary" (click)="onClose()">Cancelar</button>
            <button
              type="button"
              class="btn-primary"
              [disabled]="saving() || !name().trim()"
              (click)="createProjectAndGoToTemplate()"
            >
              {{ saving() ? 'Criando projeto...' : 'Continuar' }}
            </button>
          </div>
        } @else if (step() === 2) {
          <button type="button" class="btn-secondary wizard-footer-back" (click)="goToStepOne()">Voltar</button>
          <div class="wizard-footer-actions">
            <button type="button" class="btn-secondary" (click)="onClose()">Cancelar</button>
            <button
              type="button"
              class="btn-primary"
              [disabled]="!selectedTemplate() || !workflowName().trim()"
              (click)="goToMembersStep()"
            >
              Continuar
            </button>
          </div>
        } @else {
          <button type="button" class="btn-secondary wizard-footer-back" (click)="goToTemplateStep()">Voltar</button>
          <div class="wizard-footer-actions">
            <button type="button" class="btn-secondary" (click)="onClose()">Cancelar</button>
            <button
              type="button"
              class="btn-primary"
              [disabled]="saving() || !workflowName().trim()"
              (click)="finalizeProjectSetup()"
            >
              {{ saving() ? 'Finalizando...' : 'Finalizar cadastro' }}
            </button>
          </div>
        }
      </div>
    </app-modal>
  `,
  styles: [
    `
      .wizard-body {
        display: grid;
        gap: 14px;
      }

      .phase-inline {
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }

      .phase-inline-label {
        font-size: 12px;
        color: var(--muted);
        font-weight: 600;
      }

      .phase-inline-track {
        display: inline-flex;
        align-items: center;
        gap: 5px;
      }

      .phase-inline-dot {
        width: 18px;
        height: 18px;
        border-radius: 999px;
        border: 1px solid var(--border);
        background: var(--bg-surface);
        color: var(--muted);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 600;
      }

      .phase-inline-dot.is-active {
        border-color: color-mix(in srgb, #111827 26%, var(--border));
        background: color-mix(in srgb, #111827 10%, var(--bg-surface));
        color: #111827;
      }

      .phase-inline-line {
        width: 16px;
        height: 1px;
        background: var(--border);
      }

      .wizard-section {
        display: grid;
        gap: 14px;
      }

      .section-title {
        margin: 0;
        font-size: 15px;
        font-weight: 700;
        color: var(--text);
      }

      .icon-name-row {
        position: relative;
        display: grid;
        grid-template-columns: 44px minmax(0, 1fr);
        gap: 8px;
      }

      .icon-preview-btn {
        height: 42px;
        border-radius: 10px;
        border: 1px solid var(--border);
        color: #fff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
      }

      .icon-preview-glyph {
        color: var(--text);
      }

      .icon-preview-btn.has-color .icon-preview-glyph {
        color: #fff;
      }

      .icon-preview-empty-glyph {
        color: #9ca3af;
      }



      .icon-preview-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .field-input,
      .permission-select,
      .field-textarea {
        width: 100%;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        color: var(--text);
        font-size: 14px;
      }

      .field-input,
      .permission-select {
        height: 42px;
        padding: 0 12px;
      }

      .field-textarea {
        resize: vertical;
        min-height: 56px;
        padding: 10px 12px;
      }

      .field-label {
        display: grid;
        gap: 6px;
        font-size: 13px;
        font-weight: 600;
        color: var(--text);
      }

      .optional {
        color: var(--muted);
        font-weight: 500;
      }

      .field-help {
        font-size: 11px;
        font-weight: 500;
        color: var(--muted);
      }

      .inline-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }

      .inline-title {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: var(--text);
      }

      .inline-subtitle {
        margin: 2px 0 0;
        font-size: 13px;
        color: var(--muted);
      }

      .switch input {
        display: none;
      }

      .switch-track {
        width: 40px;
        height: 24px;
        border-radius: 999px;
        background: #d1d5db;
        display: inline-flex;
        align-items: center;
        padding: 2px;
        transition: background-color 120ms ease;
      }

      .switch-thumb {
        width: 20px;
        height: 20px;
        border-radius: 999px;
        background: #fff;
        transform: translateX(0);
        transition: transform 120ms ease;
      }

      .switch input:checked + .switch-track {
        background: #4f46e5;
      }

      .switch input:checked + .switch-track .switch-thumb {
        transform: translateX(16px);
      }

      .icon-picker {
        position: fixed;
        z-index: 60;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: min(460px, calc(100vw - 24px));
        max-height: calc(100vh - 180px);
        overflow: auto;
        border: 1px solid var(--border);
        border-radius: 12px;
        background: var(--bg-surface);
        box-shadow: 0 8px 18px rgba(15, 23, 42, 0.14);
        padding: 12px;
        display: grid;
        gap: 10px;
      }

      .icon-picker-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .picker-head-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .icon-picker-head strong {
        font-size: 12px;
      }

      .icon-picker-head button:not(.upload-btn) {
        border: 0;
        background: transparent;
        color: var(--muted);
        font-size: 11px;
        cursor: pointer;
      }

      .icon-picker-head .upload-btn.head-upload-btn {
        height: 26px;
        padding: 0 10px;
        font-size: 11px;
      }

      .icon-search {
        height: 36px;
      }

      .icon-grid {
        display: grid;
        grid-template-columns: repeat(10, minmax(0, 1fr));
        gap: 4px;
        max-height: 170px;
        overflow: auto;
        padding-right: 3px;
      }

      .icon-choice {
        height: 26px;
        border-radius: 5px;
        border: 1px solid transparent;
        background: transparent;
        color: #6b7280;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
      }

      .icon-choice:hover {
        background: color-mix(in srgb, var(--bg-surface) 80%, #e5e7eb);
        border-color: color-mix(in srgb, #94a3b8 20%, var(--border));
        color: #4b5563;
      }

      .icon-choice.is-selected {
        color: #4338ca;
        border-color: color-mix(in srgb, #6366f1 58%, var(--border));
        background: color-mix(in srgb, #eef2ff 78%, var(--bg-surface));
        box-shadow: 0 0 0 1px color-mix(in srgb, #6366f1 30%, transparent);
      }

      .picker-divider {
        height: 1px;
        background: var(--border);
      }

      .picker-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
      }

      .picker-color-inline {
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        gap: 7px;
      }

      .picker-section-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
      }

      .picker-section-head strong {
        font-size: 13px;
      }

      .picker-section-head button {
        border: 0;
        background: transparent;
        color: var(--muted);
        font-size: 12px;
        cursor: pointer;
      }

      .color-grid {
        display: grid;
        grid-template-columns: repeat(6, minmax(0, 1fr));
        gap: 8px;
      }

      .color-choice {
        width: 100%;
        aspect-ratio: 1 / 1;
        border-radius: 999px;
        border: 1px solid color-mix(in srgb, var(--border) 70%, #000);
      }

      .color-choice.compact {
        width: 16px;
        height: 16px;
      }

      .color-choice-none {
        position: relative;
        background: #ffffff;
        border: 1px solid #d1d5db;
        border-radius: 999px;
        overflow: hidden;
      }

      .none-mark {
        position: absolute;
        left: 50%;
        top: 50%;
        width: 12px;
        height: 2px;
        background: #ef4444;
        border-radius: 999px;
        transform-origin: center;
        transform: translate(-50%, -50%) rotate(-45deg);
      }

      .color-choice.is-selected {
        box-shadow: 0 0 0 2px var(--bg-surface), 0 0 0 4px color-mix(in srgb, #111827 22%, transparent);
      }

      .upload-row {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .upload-btn {
        height: 32px;
        border-radius: 8px;
        border: 1px solid var(--border);
        background: color-mix(in srgb, var(--bg-surface) 90%, #f3f4f6);
        color: var(--text);
        font-size: 12px;
        font-weight: 600;
        padding: 0 12px;
        flex-shrink: 0;
      }

      .upload-btn:hover {
        background: color-mix(in srgb, var(--bg-surface) 72%, #e5e7eb);
        border-color: color-mix(in srgb, #94a3b8 30%, var(--border));
      }

      .upload-file-name {
        min-width: 0;
        font-size: 12px;
        color: var(--muted);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .upload-file-name.inline {
        display: block;
      }

      .upload-input {
        display: none;
      }

      .clear-upload-btn {
        height: 26px;
        border-radius: 7px;
        border: 1px solid var(--border);
        background: var(--bg-surface);
        color: var(--muted);
        font-size: 11px;
        padding: 0 8px;
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        border: 0;
      }

      .template-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 10px;
      }

      .template-card {
        min-height: 84px;
        border-radius: 10px;
        border: 1px solid var(--border);
        background: var(--bg-surface);
        padding: 12px;
        display: grid;
        gap: 5px;
        text-align: left;
      }

      .template-card strong {
        font-size: 14px;
        color: var(--text);
      }

      .template-card span {
        font-size: 13px;
        color: var(--muted);
      }

      .template-card.is-active {
        border-color: color-mix(in srgb, #111827 45%, var(--border));
      }

      .workflow-form-grid {
        display: grid;
        gap: 10px;
      }

      .stages-list {
        display: grid;
        gap: 8px;
      }

      .stage-row {
        border: 1px solid var(--border);
        border-radius: 10px;
        padding: 8px;
        display: grid;
        grid-template-columns: 28px minmax(0, 1fr) 110px 30px 30px 30px;
        align-items: center;
        gap: 6px;
        position: relative;
      }

      .stage-order-cell {
        position: relative;
        z-index: 5;
        width: 24px;
        height: 24px;
      }

      .stage-order {
        width: 24px;
        height: 24px;
        border-radius: 999px;
        border: 1px solid color-mix(in srgb, #111827 12%, var(--border));
        text-align: center;
        font-size: 11px;
        color: #ffffff;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .stage-order-button {
        cursor: pointer;
      }

      .stage-input {
        height: 34px;
      }

      .stage-type {
        height: 34px;
        padding: 0 8px;
      }

      .stage-color-picker-host {
        position: absolute;
        top: 50%;
        left: calc(100% + 8px);
        transform: translateY(-50%);
        z-index: 25;
      }

      .stage-order-button.light-order {
        color: #0f172a;
        border-color: color-mix(in srgb, #0f172a 18%, var(--border));
      }

      .stage-icon-btn {
        height: 30px;
        width: 30px;
        border-radius: 8px;
        border: 1px solid var(--border);
        background: var(--bg-surface);
        color: var(--text);
        font-size: 14px;
      }

      .stage-icon-btn:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }

      .stage-icon-btn.danger {
        color: #dc2626;
      }

      .stage-add-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: 8px;
        align-items: center;
      }

      .stage-helper {
        margin: 0;
        font-size: 12px;
        color: var(--muted);
      }

      .member-input-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 120px auto;
        gap: 8px;
        align-items: center;
      }

      .member-role-select {
        height: 42px;
        padding: 0 10px;
      }

      .member-chip-list {
        min-height: 72px;
        border: 1px solid var(--border);
        border-radius: 10px;
        padding: 10px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-content: flex-start;
      }

      .member-chip {
        height: 28px;
        border-radius: 999px;
        border: 1px solid var(--border);
        background: color-mix(in srgb, var(--bg-surface) 85%, #eef2ff);
        color: var(--text);
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 0 10px;
        font-size: 12px;
      }

      .member-chip-role {
        border-radius: 999px;
        border: 1px solid var(--border);
        padding: 1px 6px;
        font-size: 11px;
      }

      .member-chip-remove {
        border: 0;
        background: transparent;
        color: var(--muted);
        font-size: 14px;
        line-height: 1;
      }

      .member-empty {
        margin: 0;
        font-size: 12px;
        color: var(--muted);
      }

      .phase-note {
        margin: 0;
        font-size: 12px;
        color: var(--muted);
      }

      .wizard-footer {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
      }

      .wizard-footer-back {
        margin-right: auto;
      }

      .wizard-footer-actions {
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }

      .btn-primary,
      .btn-secondary {
        height: 36px;
        border-radius: 10px;
        border: 1px solid var(--border);
        padding: 0 14px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
      }

      .btn-secondary {
        background: var(--bg-surface);
        color: var(--text);
      }

      .btn-primary {
        border-color: #111827;
        background: #111827;
        color: #fff;
      }

      .btn-primary:hover:not(:disabled) {
        border-color: #1f2937;
        background: #1f2937;
      }

      .btn-primary:disabled {
        opacity: 0.65;
        cursor: not-allowed;
      }

      @media (max-width: 760px) {
        .template-grid {
          grid-template-columns: 1fr;
        }

        .stage-row {
          grid-template-columns: 24px minmax(0, 1fr) 1fr 32px 32px 32px;
        }
      }
    `,
  ],
})
export class CreateProjectModalComponent {
  private readonly http = inject(HttpClient);
  private readonly i18n = inject(I18nService);
  private readonly toast = inject(ToastService);
  private readonly auth = inject(AuthService);
  readonly t = this.i18n.t.bind(this.i18n);

  readonly visible = input<boolean>(false);
  readonly closed = output<void>();
  readonly created = output<string>();

  readonly step = signal<WizardStep>(1);
  readonly phases: WizardPhase[] = [{ id: 1 }, { id: 2 }, { id: 3 }];
  readonly name = signal('');
  readonly description = signal('');
  readonly isPrivate = signal(false);
  readonly selectedIcon = signal<string | null>(null);
  readonly selectedColor = signal<string | null>(null);
  readonly isIconPickerOpen = signal(false);
  readonly uploadedImageDataUrl = signal<string | null>(null);
  readonly uploadedImageName = signal('');
  readonly uploadedProjectImageFile = signal<File | null>(null);
  readonly createdProjectId = signal('');
  readonly selectedTemplate = signal<string>('starter');
  readonly workflowName = signal('Backlog');
  readonly workflowDescription = signal('Workflow principal');
  readonly defaultDueInDays = signal<number | null>(null);
  readonly workflowColorHex = signal<string | null>(null);
  readonly memberEmailInput = signal('');
  readonly memberRoleInput = signal<ProjectMemberRole>('Editor');
  readonly members = signal<MemberDraft[]>([]);
  readonly newStageName = signal('');
  readonly stages = signal<StageDraft[]>(this.getDefaultStages('starter'));
  readonly templates = signal<WorkflowTemplateOption[]>(WORKFLOW_TEMPLATES);
  readonly saving = signal(false);
  readonly openedStageColorPickerId = signal<string | null>(null);

  readonly colorOptions = WORKFLOW_ACCENT_COLOR_PALETTE;
  readonly stageColorFallback = '#4F46E5';
  readonly isLightHexColor = isLightHexColor;
  readonly filteredIcons = computed(() => [...WORKFLOW_FORM_ICON_KEYS]);
  readonly selectedTemplateTitle = computed(
    () => this.templates().find((item) => item.id === this.selectedTemplate())?.title ?? 'Inicial'
  );
  readonly modalTitle = computed(() => {
    if (this.step() === 1) return 'Criar projeto';
    if (this.step() === 2) return 'Escolher template';
    return 'Adicionar membros';
  });
  readonly modalSubtitle = computed(() => {
    if (this.step() === 1) return 'Cadastre os dados principais do projeto.';
    if (this.step() === 2) return 'Escolha o template conforme a spec do projeto.';
    return 'Defina os membros que vão participar do projeto.';
  });

  constructor() {
    effect(() => {
      if (this.visible()) return;
      this.resetWizard();
    });

    effect(() => {
      const templateId = this.selectedTemplate();
      this.applyTemplatePreset(templateId);
    });
  }

  onClose(): void {
    this.closed.emit();
  }

  toggleIconPicker(): void {
    this.isIconPickerOpen.update((value) => !value);
  }

  selectIcon(icon: string): void {
    this.selectedIcon.set(icon);
    this.uploadedImageDataUrl.set(null);
    this.uploadedImageName.set('');
    this.uploadedProjectImageFile.set(null);
  }

  resetVisualIdentity(): void {
    this.selectedIcon.set(null);
    this.selectedColor.set(null);
    this.removeUploadedImage();
  }

  removeUploadedImage(): void {
    this.uploadedImageDataUrl.set(null);
    this.uploadedImageName.set('');
    this.uploadedProjectImageFile.set(null);
  }

  onProjectImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const imageDataUrl = typeof reader.result === 'string' ? reader.result : null;
      if (!imageDataUrl) return;
      this.uploadedImageDataUrl.set(imageDataUrl);
      this.uploadedImageName.set(file.name);
      this.uploadedProjectImageFile.set(file);
      this.selectedIcon.set(null);
    };
    reader.readAsDataURL(file);
  }

  goToStepOne(): void {
    this.step.set(1);
  }

  goToTemplateStep(): void {
    this.step.set(2);
  }

  goToMembersStep(): void {
    this.step.set(3);
  }

  addMember(): void {
    const email = this.memberEmailInput().trim().toLowerCase();
    if (!email || !this.isValidEmail(email)) return;
    const role = this.memberRoleInput();
    this.members.update((prev) => (prev.some((item) => item.email === email) ? prev : [...prev, { email, role }]));
    this.memberEmailInput.set('');
    this.memberRoleInput.set('Editor');
  }

  removeMember(email: string): void {
    this.members.update((prev) => prev.filter((item) => item.email !== email));
  }

  addStage(): void {
    const nextName = this.newStageName().trim();
    if (!nextName) return;
    this.stages.update((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name: nextName,
        colorHex: '#4F46E5',
        stageType: 2,
        order: prev.length + 1,
      },
    ]);
    this.newStageName.set('');
  }

  removeStage(stageId: string): void {
    this.stages.update((prev) => {
      const next = prev.filter((item) => item.id !== stageId);
      return this.withNormalizedOrders(next);
    });
  }

  moveStage(index: number, direction: -1 | 1): void {
    const items = [...this.stages()];
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const current = items[index];
    items[index] = items[target];
    items[target] = current;
    this.stages.set(this.withNormalizedOrders(items));
  }

  updateStageName(stageId: string, name: string): void {
    this.stages.update((prev) => prev.map((item) => (item.id === stageId ? { ...item, name } : item)));
  }

  updateStageType(stageId: string, stageType: 1 | 2 | 3): void {
    this.stages.update((prev) => prev.map((item) => (item.id === stageId ? { ...item, stageType } : item)));
  }

  updateStageColor(stageId: string, colorHex: string): void {
    this.stages.update((prev) => prev.map((item) => (item.id === stageId ? { ...item, colorHex } : item)));
  }

  toggleStageColorPicker(stageId: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.openedStageColorPickerId.update((current) => (current === stageId ? null : stageId));
  }

  closeStageColorPicker(): void {
    this.openedStageColorPickerId.set(null);
  }

  onDefaultDueInDaysChange(value: number | string | null): void {
    if (value === null || value === '') {
      this.defaultDueInDays.set(null);
      return;
    }
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) {
      this.defaultDueInDays.set(null);
      return;
    }
    this.defaultDueInDays.set(Math.trunc(parsed));
  }

  async createProjectAndGoToTemplate(): Promise<void> {
    if (!this.name().trim()) return;
    this.step.set(2);
    await this.loadTemplates();
  }

  async finalizeProjectSetup(): Promise<void> {
    if (!this.workflowName().trim()) return;
    const normalizedDefaultDueInDays = this.normalizeDefaultDueInDays();
    if (normalizedDefaultDueInDays === undefined) {
      this.toast.show('Prazo padrão deve ser maior ou igual a zero.', 'error');
      return;
    }
    const stages = this.withNormalizedOrders(this.stages()).filter((stage) => stage.name.trim());
    if (!stages.length) {
      this.toast.show('Adicione ao menos um status para o workflow.', 'error');
      return;
    }

    this.saving.set(true);
    try {
      const projectId = await this.ensureProjectCreated();
      if (!projectId) throw new Error('Projeto sem id retornado');

      const workflowResponse = await firstValueFrom(
        this.http.post<CreateWorkflowResponse>('/api/workflows', {
          projectId,
          name: this.workflowName().trim(),
          description: this.workflowDescription().trim() || null,
          ...(normalizedDefaultDueInDays !== null ? { defaultDueInDays: normalizedDefaultDueInDays } : {}),
          iconKey: workflowIconKeyToApi(this.selectedIcon()),
          colorHex: this.workflowColorHex(),
        })
      );
      const workflowId = workflowResponse?.id ?? '';
      if (!workflowId) throw new Error('Workflow sem id retornado');

      for (const stage of stages) {
        await firstValueFrom(
          this.http.post(`/api/workflows/${workflowId}/stages`, {
            workflowId,
            name: stage.name.trim(),
            colorHex: stage.colorHex || '#4F46E5',
            order: stage.order,
            stageType: stage.stageType,
          })
        );
      }

      await this.syncProjectMembers(projectId);

      this.toast.success('Projeto e workflow criados com sucesso.');
      this.created.emit(projectId);
    } catch (error) {
      if (this.isUnauthorized(error)) {
        this.toast.show('Sua sessão expirou. Faça login novamente.', 'error');
      } else {
        this.toast.show('Não foi possível finalizar o cadastro do workflow.', 'error');
      }
    } finally {
      this.saving.set(false);
    }
  }

  private async ensureProjectCreated(): Promise<string | null> {
    if (this.createdProjectId()) return this.createdProjectId();
    const iconKey = projectIconKeyToApi(this.selectedIcon());
    const response = await firstValueFrom(
      this.http.post<CreateProjectResponse>('/api/projects', {
        name: this.name().trim(),
        description: this.description().trim() || null,
        coverImageUrl: null,
        colorHex: this.selectedColor(),
        ...(iconKey ? { iconKey } : {}),
        isPrivate: this.isPrivate(),
        sortOrder: null,
      })
    );
    const projectId = response?.id ?? null;
    if (projectId) {
      this.createdProjectId.set(projectId);
      await this.uploadProjectCoverIfNeeded(projectId);
    }
    return projectId;
  }

  private async uploadProjectCoverIfNeeded(projectId: string): Promise<void> {
    const file = this.uploadedProjectImageFile();
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    await firstValueFrom(this.http.post(`/api/projects/${projectId}/cover`, formData));
  }

  private async loadTemplates(): Promise<void> {
    try {
      const response = await firstValueFrom(this.http.get<unknown>('/api/workflow-templates'));
      const items = this.asArray((this.asRecord(response)['items'] ?? response) as unknown);
      const mapped = items
        .map((item) => this.asRecord(item))
        .map((item) => {
          const id = this.readString(item['id'])?.trim();
          const name = this.readString(item['name'])?.trim();
          const description = this.readString(item['description'])?.trim();
          if (!id || !name) return null;
          return {
            id,
            title: this.translateTemplateName(name),
            description: this.translateTemplateDescription(name, description),
          } as WorkflowTemplateOption;
        })
        .filter((item): item is WorkflowTemplateOption => !!item)
        .filter((item) => item.title !== 'Criar do zero');

      const sorted = mapped.sort((a, b) => this.templateOrderIndex(a.title) - this.templateOrderIndex(b.title));
      this.templates.set(sorted.length ? sorted : WORKFLOW_TEMPLATES);
    } catch {
      this.templates.set(WORKFLOW_TEMPLATES);
    }
  }

  private applyTemplatePreset(templateId: string): void {
    const normalized = this.normalizeTemplateId(templateId);
    this.workflowName.set(normalized === 'custom' ? 'Workflow principal' : this.getDefaultWorkflowName(normalized));
    this.workflowDescription.set('Workflow principal');
    this.defaultDueInDays.set(null);
    this.stages.set(this.getDefaultStages(normalized));
  }

  private resetWizard(): void {
    this.isIconPickerOpen.set(false);
    this.openedStageColorPickerId.set(null);
    this.step.set(1);
    this.createdProjectId.set('');
    this.name.set('');
    this.description.set('');
    this.isPrivate.set(false);
    this.selectedIcon.set(null);
    this.selectedColor.set(null);
    this.workflowColorHex.set(null);
    this.workflowName.set('Backlog');
    this.workflowDescription.set('Workflow principal');
    this.defaultDueInDays.set(null);
    this.selectedTemplate.set('starter');
    this.stages.set(this.getDefaultStages('starter'));
    this.memberEmailInput.set('');
    this.memberRoleInput.set('Editor');
    this.members.set([]);
    this.newStageName.set('');
    this.removeUploadedImage();
  }

  private normalizeDefaultDueInDays(): number | null | undefined {
    const value = this.defaultDueInDays();
    if (value == null) return null;
    if (!Number.isFinite(value)) return null;
    const normalized = Math.trunc(value);
    if (normalized < 0) return undefined;
    return normalized;
  }

  private normalizeTemplateId(templateId: string): string {
    const id = templateId.toLowerCase();
    if (id.includes('marketing')) return 'marketing';
    if (id.includes('project-management') || id.includes('project management')) return 'project-management';
    if (id.includes('product-engineering') || id.includes('product + engineering')) return 'product-engineering';
    if (id.includes('custom') || id.includes('zero')) return 'custom';

    const selected = this.templates().find((item) => item.id === templateId);
    if (selected) {
      const title = selected.title.trim().toLowerCase();
      if (title.includes('marketing')) return 'marketing';
      if (title.includes('gestão de projetos') || title.includes('project management')) return 'project-management';
      if (title.includes('produto + engenharia') || title.includes('product + engineering')) return 'product-engineering';
      if (title.includes('inicial') || title.includes('starter')) return 'starter';
    }

    return 'starter';
  }

  private getDefaultWorkflowName(templateId: string): string {
    if (templateId === 'marketing') return 'Planejamento de campanhas';
    if (templateId === 'project-management') return 'Backlog';
    if (templateId === 'product-engineering') return 'Roadmap';
    return 'Backlog';
  }

  private getDefaultStages(templateId: string): StageDraft[] {
    const namesByTemplate: Record<string, string[]> = {
      starter: ['TO DO', 'IN PROGRESS', 'COMPLETE'],
      marketing: ['Ideias', 'Planejado', 'Em execução', 'Revisão', 'Publicado'],
      'project-management': ['Backlog', 'Em andamento', 'Em revisão', 'Concluído'],
      'product-engineering': ['Backlog', 'Pronto para desenvolvimento', 'Em desenvolvimento', 'QA', 'Concluído'],
      custom: ['TO DO', 'IN PROGRESS', 'COMPLETE'],
    };
    const names = namesByTemplate[templateId] ?? namesByTemplate['starter'];
    return names.map((name, index) => ({
      id: crypto.randomUUID(),
      name,
      colorHex: PRIMARY_HEX_PALETTE[index % PRIMARY_HEX_PALETTE.length],
      order: index + 1,
      stageType: index === 0 ? 1 : index === names.length - 1 ? 3 : 2,
    }));
  }

  private withNormalizedOrders(items: StageDraft[]): StageDraft[] {
    return items.map((item, index) => ({ ...item, order: index + 1 }));
  }

  private async syncProjectMembers(projectId: string): Promise<void> {
    const creatorId = (this.auth.user()?.id ?? '').trim();
    if (creatorId) {
      await this.upsertProjectMember(projectId, creatorId, 'Owner');
    }

    if (!this.members().length) return;

    let users = await this.loadCompanyUsers();

    for (const member of this.members()) {
      let user = users.find((item) => item.email.toLowerCase() === member.email.toLowerCase());

      if (!user) {
        await this.inviteUser(member.email);
        users = await this.loadCompanyUsers();
        user = users.find((item) => item.email.toLowerCase() === member.email.toLowerCase());
      }

      if (!user) continue;
      await this.upsertProjectMember(projectId, user.id, member.role);
    }
  }

  private async loadCompanyUsers(): Promise<CompanyUser[]> {
    try {
      const response = await firstValueFrom(this.http.get<unknown>('/users'));
      const source = this.asRecord(response)['items'] ?? response;
      const items = this.asArray(source);
      return items
        .map((item) => this.asRecord(item))
        .map((item) => ({
          id: this.readString(item['id'])?.trim() ?? '',
          email: this.readString(item['email'])?.trim() ?? '',
          name: this.readString(item['name'])?.trim() ?? '',
        }))
        .filter((item) => !!item.id && !!item.email);
    } catch {
      return [];
    }
  }

  private async inviteUser(email: string): Promise<void> {
    try {
      await firstValueFrom(this.http.post('/users/invite', { email, role: 'Member' }));
      this.toast.show(`Convite enviado para ${email}.`, 'success');
    } catch {
      // Ignora erro para não bloquear finalização do projeto.
    }
  }

  private async upsertProjectMember(projectId: string, userId: string, role: ProjectMemberRole): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(`/api/projects/${projectId}/members`, {
          userId,
          role,
        })
      );
      return;
    } catch {
      // Se já existir, tenta atualizar papel.
    }

    try {
      await firstValueFrom(
        this.http.put(`/api/projects/${projectId}/members/${userId}`, {
          role,
        })
      );
    } catch {
      // Não impede a criação do fluxo principal.
    }
  }

  private translateTemplateName(name: string): string {
    const normalized = name.trim().toLowerCase();
    if (normalized === 'starter') return 'Inicial';
    if (normalized === 'marketing teams') return 'Marketing';
    if (normalized === 'project management') return 'Gestão de Projetos';
    if (normalized === 'product + engineering') return 'Produto + Engenharia';
    return name;
  }

  private translateTemplateDescription(name: string, fallback?: string | null): string {
    const normalized = name.trim().toLowerCase();
    if (normalized.includes('starter') || normalized.includes('inicial')) {
      return this.t('workflows.templates.descriptions.starter');
    }
    if (normalized.includes('marketing')) {
      return this.t('workflows.templates.descriptions.marketing');
    }
    if (normalized.includes('project') || normalized.includes('gestão de projetos')) {
      return this.t('workflows.templates.descriptions.projectmanagement');
    }
    if (normalized.includes('product') || normalized.includes('produto')) {
      return this.t('workflows.templates.descriptions.productengineering');
    }
    return (fallback ?? '').trim() || this.t('workflows.templates.descriptions.default');
  }

  private templateOrderIndex(title: string): number {
    const normalized = title.trim().toLowerCase();
    if (normalized === 'inicial') return 0;
    if (normalized === 'marketing') return 1;
    if (normalized === 'gestão de projetos') return 2;
    if (normalized === 'produto + engenharia') return 3;
    return 99;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  }

  private asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private readString(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
  }

  private isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  private isUnauthorized(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 401;
  }

  roleLabel(role: ProjectMemberRole): string {
    if (role === 'Owner') return this.t('projects.members.roles.owner');
    if (role === 'Editor') return this.t('projects.members.roles.editor');
    return this.t('projects.members.roles.viewer');
  }
}
